/**
 * NVIDIA NIM free-tier RPM capacity gauge (2026-08-16, ao_open_work_
 * consolidated_tracker "build the rest now" -- NVIDIA capacity-tracking layer).
 * ONE shared gauge, not one row per account, unlike GeminiCapacityPanel --
 * NVIDIA's registered accounts (nvidia-diffusiongemma, nvidia-gemma-4-31b-it)
 * share ONE API key with a GLOBAL per-key RPM ceiling, so there is exactly one
 * pool to show, with both accounts listed as sharing it (see the backend
 * nvidia_headroom.py module docstring).
 *
 * DORMANT: both NVIDIA accounts stay paused per standing operator instruction
 * (task-routing logic doesn't exist yet), so this always renders 0 used until
 * that changes -- an honest "no data yet" state, not a fabricated number. The
 * 40 RPM ceiling itself is a community-reported baseline, not yet empirically
 * confirmed against this fleet's own key -- rendered with an explicit caveat,
 * not silently presented as settled fact.
 */

import { useEffect, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import type { AccountView, NvidiaCapacityView } from "./types";

const POLL_INTERVAL_MS = 30000;

/** Real, per-account, evidence-backed degradation flag — NOT a hardcoded model-name
 * check (ao ask 2026-08-19). Derived live from GET /api/accounts' `health_status` field
 * (accounts.json AccountDef.health_status), so this list empties on its own the
 * moment gemma_4_31b_it_persistent_timeout_2026_08_19 resolves and the operator
 * flips the account back to "healthy" — never a static frontend allow/deny list. */
export function degradedNvidiaAccounts(accounts: AccountView[]): AccountView[] {
  return accounts.filter((a) => a.provider === "nvidia" && a.health_status === "degraded");
}

export const GEMMA_DEGRADED_REASON =
  "Real, reproduced failure (6 real API attempts, 3 keys, streaming/non-streaming, direct and via proxy — all " +
  "zero-byte timeouts) — see gemma_4_31b_it_persistent_timeout_2026_08_19.md. Do not dispatch to this model until " +
  "resolved; google/diffusiongemma-26b-a4b-it is the proven-working Gemma alternative.";

function usagePct(used: number, ceiling: number): number {
  return ceiling <= 0 ? 0 : Math.min(100, (used / ceiling) * 100);
}

function GaugeBar({ used, ceiling }: { used: number; ceiling: number }) {
  const pct = usagePct(used, ceiling);
  const tone =
    pct >= 90 ? "capacity-gauge-fill-danger" : pct >= 60 ? "capacity-gauge-fill-warn" : "capacity-gauge-fill-ok";
  return (
    <div className="capacity-gauge-track">
      <div className={`capacity-gauge-fill ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function NvidiaCapacityPanel({ baseUrl }: { baseUrl: string }) {
  const [data, setData] = useState<NvidiaCapacityView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [degraded, setDegraded] = useState<AccountView[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.getNvidiaCapacity(baseUrl);
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  useEffect(() => {
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const accounts = await api.listAccounts(baseUrl);
        if (!cancelled) setDegraded(degradedNvidiaAccounts(accounts));
      } catch {
        // Same "degrade quietly, never blank the panel" contract as the capacity
        // fetch above — a failed account-list probe just skips the banner.
      }
    };
    void loadAccounts();
    const id = setInterval(() => void loadAccounts(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  return (
    <Panel title="NVIDIA Capacity" collapsible tone="accounts">
      <div className="usage-panel-hint">
        NVIDIA's free tier has no $ balance — like Gemini, the real ceiling is a raw request-rate quota. Unlike Gemini,
        it's GLOBAL PER KEY: every NVIDIA account listed below shares the SAME RPM pool, so dispatching them
        concurrently does not multiply available capacity, it just draws the shared ceiling down faster.
      </div>
      {degraded.length > 0 && (
        <div className="gemma-degraded-banner" title={GEMMA_DEGRADED_REASON}>
          ⚠️ <strong>Degraded: {degraded.map((a) => a.label).join(", ")}</strong> — hover for why
        </div>
      )}
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {data === undefined && !error && <div className="task-usage-pending">loading…</div>}
      {data === null && !error && <div className="task-usage-pending">no NVIDIA accounts registered</div>}
      {data && !error && (
        <div className="capacity-account">
          <div className="capacity-account-header">
            <span className="mono">{data.account_labels.join(" + ")}</span>
            <span className="capacity-account-meta">
              shared key{data.ceiling_confirmed ? "" : " · ceiling unconfirmed"}
            </span>
          </div>
          <table className="acc-usage-table acc-usage-table-breakdown">
            <tbody>
              <tr>
                <td className="acc-usage-window-label">RPM (shared)</td>
                <td className="mono">
                  {data.rpm_used} / {data.rpm_ceiling}
                </td>
                <td>
                  <GaugeBar used={data.rpm_used} ceiling={data.rpm_ceiling} />
                </td>
              </tr>
            </tbody>
          </table>
          {!data.ceiling_confirmed && (
            <div className="acc-usage-note">
              {data.rpm_ceiling} RPM is a community-reported baseline, not yet empirically confirmed against this
              fleet's own key.
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
